import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface RuntimeConfigResponse {
  googleMapsApiKey: string;
  baseIamUrl?: string;
  agentsEmbedUrl?: string;
}

/**
 * Bounded so a dropped/black-holed connection can never hang the app-boot APP_INITIALIZER
 * indefinitely -- ApplicationInitStatus awaits every multi:true initializer before ANY component
 * (including the pre-auth login page) is created, so an unbounded fetch here would be a full-app
 * outage, not a degraded map feature. Mirrors the existing timeout() usage at
 * onboarding-overview.component.ts.
 */
const RUNTIME_CONFIG_FETCH_TIMEOUT_MS = 4000;

const EMPTY_RUNTIME_CONFIG: RuntimeConfigResponse = { googleMapsApiKey: '', baseIamUrl: '', agentsEmbedUrl: '' };

/**
 * Fetches `/api/public/runtime-config` once (pre-auth, via the APP_INITIALIZER) and caches the
 * result for the lifetime of the app. Every consumer of the browser Google Maps key reads from
 * this service instead of a build-time environment literal.
 */
@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService {
  private googleMapsApiKey = '';

  constructor(private http: HttpClient) {}

  /**
   * Always resolves -- never rejects. A timeout, network error, or non-2xx response all fall back
   * to an empty key (logged once) rather than blocking app boot or surfacing an unhandled rejection
   * to the APP_INITIALIZER.
   */
  load(): Promise<void> {
    return firstValueFrom(
      this.http.get<RuntimeConfigResponse>(`${environment.apiPublicUrl}/runtime-config`).pipe(
        timeout(RUNTIME_CONFIG_FETCH_TIMEOUT_MS),
        catchError(error => {
          console.warn('Failed to load runtime config -- Google Maps features will be unavailable.', error);
          return of(EMPTY_RUNTIME_CONFIG);
        }),
      ),
    ).then(response => {
      this.googleMapsApiKey = response.googleMapsApiKey ?? '';
      this.applyBaseIamUrl(response.baseIamUrl);
      this.applyAgentsEmbedUrl(response.agentsEmbedUrl);
    });
  }

  getGoogleMapsApiKey(): string {
    return this.googleMapsApiKey;
  }

  /**
   * BRN-2148 -- adopt a server-supplied IAM origin when there is one, otherwise leave the bundle's
   * own value alone.
   *
   * `environment.baseIamUrl` is evaluated at bundle load, before this APP_INITIALIZER runs, and its
   * production value is `deriveBaseIamUrl(window.location.hostname)` -- which only produces a real
   * origin for a `<tenant>.<prefix.>brain.<domain>` host. Off such a host (a local stack, an IP, a
   * preview URL) it collapses to `https://<hostname>` and every IAM call fails, taking sign-in and
   * both SSO buttons with it. Overwriting the field (rather than routing every consumer through this
   * service) is deliberate: `AuthenticationService`, `BearerInterceptor` and `users-iam.component`
   * all read it per call, so they pick the corrected origin up with no further change, and the SSO
   * hrefs are built per render.
   *
   * An absent or empty value is NOT an override -- that is the QA/prod case, where the derivation is
   * correct and must stay untouched.
   */
  private applyBaseIamUrl(baseIamUrl: string | undefined): void {
    const injected = (baseIamUrl ?? '').trim().replace(/\/+$/, '');
    if (!injected) {
      return;
    }
    environment.baseIamUrl = injected;
  }

  /**
   * BRN-2148 -- same contract as applyBaseIamUrl above, for the brain-agents embed origin.
   *
   * `environment.agentsEmbedUrl` is `deriveAgentsEmbedUrl(window.location.hostname)` in a production
   * bundle, which collapses to `https://localhost` off a `<tenant>.<prefix.>brain.<domain>` host. The
   * AI-chat launcher is availability-gated on a real `GET <agentsEmbedUrl>/embed/health` probe, so a
   * bundle that cannot name brain-agents-web probes an origin that does not exist and the launcher is
   * permanently absent -- the gate itself is correct, its input was not. AgentEmbedService reads
   * `environment.agentsEmbedUrl` per probe/mount/postMessage check, and this APP_INITIALIZER completes
   * before the first probe is scheduled, so overwriting the field reaches every consumer.
   *
   * An absent or empty value is NOT an override -- that is the QA/prod case, where the derivation is
   * correct and must stay untouched.
   */
  private applyAgentsEmbedUrl(agentsEmbedUrl: string | undefined): void {
    const injected = (agentsEmbedUrl ?? '').trim().replace(/\/+$/, '');
    if (!injected) {
      return;
    }
    environment.agentsEmbedUrl = injected;
  }
}
